import { useState, type ReactNode } from 'react';
import { AlertTriangle, Scale, Trash2 } from 'lucide-react';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useFxPeriodEndWorking } from '@/hooks/react-query/useCurrencyConversions';
import {
  FUND_TRANSACTION_TYPE_LABELS,
  type FundTransactionDetail,
  type ReconciliationStateResponse,
} from '@/types/fund-transactions';
import type {
  AmountMovedAtPosting,
  FxPeriodEndPreviewLine,
} from '@/types/fx-period-end';
import { formatCurrency, formatDate, formatFxRate } from '@/utils/formatters';

import { Field } from './transactionDetails';
import { TransactionDetailsCard } from './TransactionDetailsCard';

/**
 * A quarter close, and the arithmetic behind it.
 *
 * A period end has no document behind it, so the working is the evidence: per
 * currency, the balance held, the rate applied, and the carrying value either
 * side of the move. The backend picks its source from the transaction's status
 * (draft = previewed, posted = read off the lines it wrote).
 */
export function FxPeriodEndDetailsCard({
  transaction,
  fundId,
  fundName,
  reconciliation,
  onViewJournalEntries,
  onDiscard,
}: {
  transaction: FundTransactionDetail;
  fundId: string;
  fundName: string;
  reconciliation: ReconciliationStateResponse | undefined;
  onViewJournalEntries?: () => void;
  /** Required: the page owns the mutation, because the row it is showing goes. */
  onDiscard: () => Promise<void>;
}) {
  const [isConfirmingDiscard, setIsConfirmingDiscard] = useState(false);
  const snapshot = transaction.fx_period_end;
  const periodEndDate =
    snapshot?.period_end_date ?? transaction.transaction_date;
  const isAwaitingApproval = transaction.status === 'DRAFT';
  const working = useFxPeriodEndWorking(fundId, transaction.id);

  const baseCurrency = transaction.currency ?? undefined;
  const lines = working.data?.lines ?? [];
  const previewedAmount = snapshot ? Number(snapshot.previewed_amount) : null;
  const netResult = working.data ? Number(working.data.net_fx_result) : null;
  const hasDrifted =
    netResult !== null &&
    previewedAmount !== null &&
    netResult.toFixed(2) !== previewedAmount.toFixed(2);
  // Unlike hasDrifted, this is the ledger moving *after* approval -- only an
  // earlier quarter closed afterwards causes it.
  const approvedAmount =
    working.data?.amount_as_approved != null
      ? Number(working.data.amount_as_approved)
      : null;
  // Tells "no foreign currency at all" apart from "residual absorbed since".
  const hadPreviewedMovement =
    previewedAmount !== null && previewedAmount !== 0;
  // Server's statement that this posting booked a figure other than the
  // approved one.
  const amountMovedAtPosting = reconciliation?.amount_moved_at_posting ?? null;
  // The posted-case sentence duplicates the disclosure, so it stands down once
  // that renders -- and again for a restatement, which moved the figure after
  // approval, not between drafting and approval. The draft-case one warns about
  // approving, so it always holds.
  const showDriftNote =
    hasDrifted &&
    (isAwaitingApproval ||
      (amountMovedAtPosting === null && approvedAmount === null));

  return (
    <>
      <TransactionDetailsCard
        transaction={transaction}
        fundName={fundName}
        reconciliation={reconciliation}
        onViewJournalEntries={onViewJournalEntries}
        icon={<Scale className="h-5 w-5" />}
        iconClassName="bg-amber-100 text-amber-600 dark:bg-amber-950/40 dark:text-amber-300"
        eyebrow={FUND_TRANSACTION_TYPE_LABELS[transaction.transaction_type]}
        actions={
          isAwaitingApproval && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setIsConfirmingDiscard(true)}
              data-testid="discard-period-end-button"
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Discard draft
            </Button>
          )
        }
        fields={
          <>
            {amountMovedAtPosting && (
              <AmountMovedDisclosure
                amountMoved={amountMovedAtPosting}
                currency={baseCurrency}
              />
            )}
            <Field label="Period end">{formatDate(periodEndDate)}</Field>
            {netResult !== null && (
              <Field label="FX result">
                <FxResult amount={netResult} currency={baseCurrency} />
              </Field>
            )}
            {hasDrifted &&
              previewedAmount !== null &&
              amountMovedAtPosting === null && (
                <Field label="Previewed when drafted">
                  <span className="text-muted-foreground">
                    <FxResult
                      amount={previewedAmount}
                      currency={baseCurrency}
                    />
                  </span>
                </Field>
              )}
            {approvedAmount !== null && (
              <Field label="Approved at">
                <span className="text-muted-foreground">
                  <FxResult amount={approvedAmount} currency={baseCurrency} />
                </span>
              </Field>
            )}
            <Field label="How it was reached" span="FULL_ROW">
              <WorkingGate
                isLoading={working.isLoading}
                isError={working.isError}
              >
                {approvedAmount !== null && (
                  <p className="mb-2 text-xs text-amber-600 dark:text-amber-400">
                    This quarter was restated after approval, because an earlier
                    quarter was closed afterwards. Nothing new was booked - the
                    same total is now split differently across the two periods.
                    The FX result above is what the ledger holds.
                  </p>
                )}
                {showDriftNote && (
                  <p className="mb-2 text-xs text-amber-600 dark:text-amber-400">
                    {isAwaitingApproval
                      ? 'The ledger has moved since this draft was created. Approving posts the FX result above, not the previewed one.'
                      : 'The ledger moved between this draft being created and approved. The FX result above is what posted.'}
                  </p>
                )}
                <WorkingLines
                  lines={lines}
                  hadPreviewedMovement={hadPreviewedMovement}
                  baseCurrency={baseCurrency}
                  netResult={netResult}
                />
              </WorkingGate>
            </Field>
          </>
        }
      />

      <AlertDialog
        open={isConfirmingDiscard}
        onOpenChange={setIsConfirmingDiscard}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Discard this FX period end?</AlertDialogTitle>
            <AlertDialogDescription>
              The draft for {formatDate(periodEndDate)} is deleted and that
              reporting date becomes closeable again. Nothing has been posted to
              the ledger, so there is nothing to reverse - but any later quarter
              this draft was blocking can then be closed ahead of it.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep the draft</AlertDialogCancel>
            <AlertDialogAction
              onClick={onDiscard}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Discard draft
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

/**
 * What was approved, what was booked, and why they differ.
 *
 * A disclosure, not an error: the posted figure is the correct one. It heads
 * the card and stays there -- it belongs to the row, not to the click.
 */
function AmountMovedDisclosure({
  amountMoved,
  currency,
}: {
  amountMoved: AmountMovedAtPosting;
  currency: string | undefined;
}) {
  return (
    <div
      className="col-span-2 sm:col-span-3 rounded-md border border-amber-300 bg-amber-50 p-3 dark:border-amber-900 dark:bg-amber-950/40"
      data-testid="fx-amount-moved-disclosure"
    >
      <div className="flex items-start gap-2">
        <AlertTriangle
          className="mt-0.5 h-4 w-4 shrink-0 text-amber-600 dark:text-amber-400"
          aria-hidden
        />
        <div className="space-y-1">
          <p className="text-sm font-medium text-amber-900 dark:text-amber-200">
            This close posted a different figure from the one approved.
          </p>
          <dl className="flex flex-wrap gap-x-6 gap-y-1 text-sm">
            <div>
              <dt className="text-xs font-medium text-muted-foreground">
                Approved
              </dt>
              <dd className="mt-0.5">
                <FxResult
                  amount={Number(amountMoved.approved_amount)}
                  currency={currency}
                />
              </dd>
            </div>
            <div>
              <dt className="text-xs font-medium text-muted-foreground">
                Posted
              </dt>
              <dd className="mt-0.5">
                <FxResult
                  amount={Number(amountMoved.posted_amount)}
                  currency={currency}
                />
              </dd>
            </div>
          </dl>
          <p className="text-xs text-amber-800 dark:text-amber-300">
            Approval recomputes against the ledger as it then stands, and
            something posted in the gap changed it. The posted figure is what
            the ledger holds and is the correct one; the approved figure is kept
            as the record of what was signed off.
          </p>
        </div>
      </div>
    </div>
  );
}

/**
 * An FX result in words. 4500 is an income account, so a gain reads negative --
 * showing "gain"/"loss" saves the reader decoding the sign.
 */
function FxResult({
  amount,
  currency,
}: {
  amount: number;
  currency: string | undefined;
}) {
  if (amount === 0) {
    return <>{formatCurrency(0, { currency })}</>;
  }
  const isGain = amount < 0;
  return (
    <span
      className={
        isGain
          ? 'text-emerald-600 dark:text-emerald-400'
          : 'text-red-600 dark:text-red-400'
      }
    >
      {formatCurrency(Math.abs(amount), { currency })}{' '}
      {isGain ? 'gain' : 'loss'}
    </span>
  );
}

function WorkingGate({
  isLoading,
  isError,
  children,
}: {
  isLoading: boolean;
  isError: boolean;
  children: ReactNode;
}) {
  if (isLoading) {
    return (
      <p className="text-sm text-muted-foreground">Loading the working…</p>
    );
  }
  if (isError) {
    return (
      <p className="text-sm text-destructive">
        The working could not be loaded, so nothing on this card has been
        checked against the ledger. Reload before approving - an absent working
        is not the same as an empty one.
      </p>
    );
  }
  return children;
}

function WorkingLines({
  lines,
  hadPreviewedMovement,
  baseCurrency,
  netResult,
}: {
  lines: FxPeriodEndPreviewLine[];
  hadPreviewedMovement: boolean;
  baseCurrency: string | undefined;
  netResult: number | null;
}) {
  if (lines.length > 0) {
    return (
      <WorkingTable
        lines={lines}
        baseCurrency={baseCurrency}
        netResult={netResult}
      />
    );
  }
  if (hadPreviewedMovement) {
    return (
      <p className="text-sm text-muted-foreground">
        No foreign currency remains at this date, so the working has no lines:
        the residual the draft was priced on was absorbed by something posted
        since.
      </p>
    );
  }
  return (
    <p className="text-sm text-muted-foreground">
      No foreign currency held at this date, so there is nothing to retranslate.
    </p>
  );
}

function WorkingTable({
  lines,
  baseCurrency,
  netResult,
}: {
  lines: FxPeriodEndPreviewLine[];
  baseCurrency: string | undefined;
  netResult: number | null;
}) {
  return (
    <div className="overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Currency</TableHead>
            <TableHead className="text-right">Held</TableHead>
            <TableHead className="text-right">Rate</TableHead>
            <TableHead className="text-right">Carried before</TableHead>
            <TableHead className="text-right">Carried after</TableHead>
            <TableHead className="text-right">FX result</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {lines.map((line) => (
            <TableRow key={line.account_code}>
              <TableCell>
                <div className="font-medium">{line.currency}</div>
                <div className="text-xs text-muted-foreground">
                  {line.account_code}
                </div>
              </TableCell>
              <TableCell className="text-right">
                {formatCurrency(line.foreign_balance, {
                  currency: line.currency,
                })}
              </TableCell>
              <TableCell className="text-right">
                {formatFxRate(line.rate)}
              </TableCell>
              <TableCell className="text-right">
                {formatCurrency(line.carry_before, { currency: baseCurrency })}
              </TableCell>
              <TableCell className="text-right">
                {formatCurrency(line.carry_after, { currency: baseCurrency })}
              </TableCell>
              <TableCell className="text-right">
                <FxResult
                  amount={Number(line.fx_result)}
                  currency={baseCurrency}
                />
              </TableCell>
            </TableRow>
          ))}
          {lines.length > 1 && netResult !== null && (
            <TableRow>
              <TableCell colSpan={5} className="text-right font-medium">
                Net
              </TableCell>
              <TableCell className="text-right font-medium">
                <FxResult amount={netResult} currency={baseCurrency} />
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </div>
  );
}
